import React from 'react';
import GridViewIcon from '@mui/icons-material/GridView';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import ListIcon from '@mui/icons-material/ViewHeadline';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import {
  ButtonBase,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';
import { ActionWithPayload } from '../../../../../utils/action-helper';
import { SortType } from '../../../../../interfaces/productType';
import { ILocation } from '../../../../../interfaces/locations';
import { IProductFilter } from '../../../../../interfaces/product';
import { IAppState } from '../../../../../store/rootDuck';

const ROWS_PER_PAGE_OPTIONS = [10, 50, 100];

const useStyles = makeStyles()(theme => ({
  viewButton: {
    width: 34,
    height: 34,
    marginRight: 10,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    color: '#8ca4bf',
    boxShadow: '0 6px 16px rgba(22, 34, 51, 0.08)',
    transition: 'color 150ms ease, box-shadow 150ms ease',
    '&:hover': {
      backgroundColor: '#ffffff',
      color: '#0086c8',
      boxShadow: '0 8px 18px rgba(22, 34, 51, 0.12)',
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  viewButtonActive: {
    color: theme.palette.primary.main,
  },
  viewIcon: {
    width: 20,
    height: 20,
  },
  mobileTopRow: {
    width: '100%',
    justifyContent: 'space-between',
  },
  mobileViews: {
    display: 'flex',
    alignItems: 'center',
  },
  paginationLayout: {
    width: '100%',
    minHeight: 36,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 0,
    gap: 4,
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
      overflowX: 'auto',
      paddingBottom: 2,
    },
  },
  pageButton: {
    minWidth: 32,
    height: 32,
    padding: '0 8px',
    borderRadius: '50%',
    color: '#4b5568',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1,
    '&:hover': {
      backgroundColor: '#eef2f6',
    },
    '&.Mui-disabled': {
      color: '#9aa6b7',
    },
  },
  pageButtonActive: {
    backgroundColor: 'rgb(221, 222, 225) !important;',
    color: '#1f2937',
    pointerEvents: 'none',
  },
  arrowButton: {
    minWidth: 32,
    height: 32,
    padding: 0,
    borderRadius: '50%',
    backgroundColor: 'rgb(221, 222, 225) !important',
    color: '#475569',
    '&:hover': {
      backgroundColor: '#d5dde6',
    },
    '&.Mui-disabled': {
      backgroundColor: 'transparent',
      color: '#a7b2c0',
    },
  },
  ellipsis: {
    minWidth: 28,
    textAlign: 'center',
    color: '#4b5568',
    fontSize: 13,
  },
  pageSize: {
    position: 'absolute',
    right: 0,
    top: -30,
    [theme.breakpoints.up(1050)]: {
      top: 0,
    },
    [theme.breakpoints.down('sm')]: {
      position: 'static',
      marginLeft: 12,
    },
  },
  pageSizeButton: {
    minWidth: 70,
    height: 36,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    padding: '0 7px 0 10px',
    borderRadius: 14,
    backgroundColor: '#ffffff',
    color: '#344054',
    fontSize: 14,
    fontWeight: 500,
    boxShadow: '0 8px 18px rgba(22, 34, 51, 0.08)',
  },
  pageSizeArrow: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e5ebf2',
    color: '#667085',
    '& svg': {
      width: 18,
      height: 18,
    },
  },
  pageSizeMenuPaper: {
    minWidth: 70,
    // marginTop: 6,
    borderRadius: '0 0 16px 16px',
    boxShadow: '0 18px 32px rgba(22, 34, 51, 0.14)',
    overflow: 'hidden',
    '& .MuiList-root': {
      // paddingTop: 0,
      // paddingBottom: 0,
    },
  },
  pageSizeMenuItem: {
    minHeight: 34,
    padding: '5px 10px',
    color: '#475467',
    fontSize: 14,
    justifyContent: 'flex-start',
    '&:hover': {
      backgroundColor: '#f3f6fa',
    },
  },
}));

interface ProducCatalogHeader {
  classes: any;
  fetch: (payload: {
    page: number;
    perPage: number;
    filter?: IProductFilter;
    search?: string;
    categoryId?: number;
    sort_type?: string;
    locale?: ILocation | null;
    activity?: string;
    sortType?: SortType;
  }) => any;
  page: number;
  products: any[];
  perPage: number;
  total: number;
  selectedCategoryId: number | null;
  viewType: string;
  setViewType: (
    payload: 'table' | 'grid' | 'list'
  ) => ActionWithPayload<'products-catalog/SET_VIEWTYPE', 'table' | 'grid' | 'list'>;
  isAdmin: boolean | undefined;
  beforeFetch?: () => void;
}

const getPaginationItems = (currentPage: number, totalPages: number) => {
  if (totalPages <= 6) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const items: Array<number | 'ellipsis-prev' | 'ellipsis-next'> = [1, 2, 3];
  const middleStart = Math.max(4, currentPage - 1);
  const middleEnd = Math.min(totalPages - 3, currentPage + 1);

  if (middleStart > 4) {
    items.push('ellipsis-prev');
  }

  for (let pageNumber = middleStart; pageNumber <= middleEnd; pageNumber += 1) {
    items.push(pageNumber);
  }

  if (middleEnd < totalPages - 3) {
    items.push('ellipsis-next');
  }

  items.push(totalPages - 2, totalPages - 1, totalPages);

  return items;
};

const ProductCatalogHeader = React.memo(
  ({
    classes,
    fetch,
    page,
    perPage,
    total,
    selectedCategoryId,
    setViewType,
    viewType,
    beforeFetch,
  }: ProducCatalogHeader) => {
    const searchText = useSelector((state: IAppState) => state.productsCatalog.search) ?? '';
    const { classes: headerClasses } = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [pageSizeAnchor, setPageSizeAnchor] = React.useState<null | HTMLElement>(null);
    const currentPerPage = perPage || ROWS_PER_PAGE_OPTIONS[0];
    const totalPages = Math.max(1, Math.ceil((total || 0) / currentPerPage));
    const currentPage = Math.min(Math.max(page || 1, 1), totalPages);

    const fetchRows = React.useCallback(
      (params: { page: number; perPage: number }) => {
        fetch({
          categoryId: selectedCategoryId ?? undefined,
          search: searchText,
          ...params,
        });
        beforeFetch?.();
      },
      [beforeFetch, fetch, searchText, selectedCategoryId]
    );

    const handlePageChange = (nextPage: number) => {
      if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) {
        return;
      }

      fetchRows({ page: nextPage, perPage: currentPerPage });
    };

    const handlePerPageChange = (nextPerPage: number) => {
      localStorage.setItem('productsPerPage', String(nextPerPage));
      setPageSizeAnchor(null);
      fetchRows({ page: 1, perPage: nextPerPage });
    };

    const viewOptions = [
      {
        type: 'table' as const,
        label: 'Табличный вид',
        icon: <SplitscreenIcon className={headerClasses.viewIcon} />,
      },
      {
        type: 'grid' as const,
        label: 'Плитка',
        icon: <GridViewIcon className={headerClasses.viewIcon} />,
      },
      {
        type: 'list' as const,
        label: 'Список',
        icon: <ListIcon className={headerClasses.viewIcon} />,
      },
    ];

    const viewButtons = viewOptions
      .filter(option => !isMobile || option.type !== 'list')
      .map(option => (
        <Tooltip key={option.type} title={option.label}>
          <IconButton
            aria-label={option.label}
            className={clsx(headerClasses.viewButton, {
              [headerClasses.viewButtonActive]: viewType === option.type,
            })}
            onClick={() => setViewType(option.type)}
          >
            {option.icon}
          </IconButton>
        </Tooltip>
      ));

    const pageSizeSelect = (
      <div className={headerClasses.pageSize}>
        <ButtonBase
          aria-controls={pageSizeAnchor ? 'product-catalog-page-size-menu' : undefined}
          aria-expanded={pageSizeAnchor ? 'true' : undefined}
          aria-haspopup='menu'
          className={headerClasses.pageSizeButton}
          style={pageSizeAnchor ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : undefined}
          onClick={event => setPageSizeAnchor(event.currentTarget)}
        >
          {currentPerPage}
          <span className={headerClasses.pageSizeArrow}>
            {pageSizeAnchor ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
          </span>
        </ButtonBase>
        <Menu
          id='product-catalog-page-size-menu'
          anchorEl={pageSizeAnchor}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          disableScrollLock
          onClose={() => setPageSizeAnchor(null)}
          open={Boolean(pageSizeAnchor)}
          slotProps={{
            paper: {
              className: headerClasses.pageSizeMenuPaper,
            },
          }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {ROWS_PER_PAGE_OPTIONS.map(option => (
            <MenuItem
              key={option}
              className={headerClasses.pageSizeMenuItem}
              onClick={() => handlePerPageChange(option)}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );

    return (
      <div className={classes.productColHeader} style={{ marginBottom: 15, marginTop: 15 }}>
        <div
          className={clsx(classes.productColHeaderItem, {
            [headerClasses.mobileTopRow]: isMobile,
          })}
        >
          {isMobile ? (
            <>
              <div className={headerClasses.mobileViews}>{viewButtons}</div>
              {pageSizeSelect}
            </>
          ) : (
            viewButtons
          )}
        </div>

        <div className={classes.productColHeaderItem} style={{ width: '100%', marginTop: isMobile ? 15 : 0 }}>
          <div className={headerClasses.paginationLayout}>
            <div className={headerClasses.pagination}>
              <IconButton
                aria-label='Предыдущая страница'
                className={headerClasses.arrowButton}
                disabled={currentPage <= 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                <KeyboardArrowLeftRoundedIcon />
              </IconButton>

              {getPaginationItems(currentPage, totalPages).map(item =>
                typeof item === 'number' ? (
                  <IconButton
                    key={item}
                    aria-label={`Страница ${item}`}
                    className={clsx(headerClasses.pageButton, {
                      [headerClasses.pageButtonActive]: item === currentPage,
                    })}
                    onClick={() => handlePageChange(item)}
                  >
                    {item}
                  </IconButton>
                ) : (
                  <span key={item} className={headerClasses.ellipsis}>
                    ...
                  </span>
                )
              )}

              <IconButton
                aria-label='Следующая страница'
                className={headerClasses.arrowButton}
                disabled={currentPage >= totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                <KeyboardArrowRightRoundedIcon />
              </IconButton>
            </div>
            {!isMobile && pageSizeSelect}
          </div>
        </div>
      </div>
    );
  }
);

export default ProductCatalogHeader;
